"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { FormButtonSelect } from "@/components/form/form-button-select";
import { CaloriesCalculatorTrigger } from "@/features/calories-calculator/components/calories-calculator-trigger";
import type {
  DietCatalogCalorificsType,
  DietCatalogDetail,
} from "@/features/diets/types";
import type { ConfigureDietReturnFormType } from "../../forms/configure-diet";
import { FormSection } from "./configure-form-section";

export const ConfigureVariants = ({
  form,
  diet,
}: {
  form: ConfigureDietReturnFormType;
  diet: DietCatalogDetail;
}) => {
  const t = useTranslations();
  const variant = form.watch("variant");
  const [calorifics, setCalorifics] = useState<DietCatalogCalorificsType[]>([]);

  useEffect(() => {
    if (!diet.variants) return;

    const currentVariant = diet.variants.find((item) => item.id === variant);
    if (!currentVariant) return;

    // set available calorifics
    setCalorifics(
      (currentVariant.calorifics as unknown as DietCatalogCalorificsType[]) ||
        [],
    );
  }, [variant]);

  const handleChangeMeals = (variantId: string) => {
    if (!variantId) return;

    const newVariant = diet.variants?.find((item) => item.id === variantId);
    form.setValue(
      "meals",
      newVariant?.mealTypes.map((mealType) => mealType.id) || [""],
    );

    const previousKcal = Number(form.getValues("kcal"));
    const closestCalories = (newVariant?.calorifics ?? [])
      .map((calorific) => calorific.calories)
      .sort((a, b) => a - b)
      .reduce<number | undefined>(
        (closest, calories) =>
          closest === undefined ||
          Math.abs(calories - previousKcal) < Math.abs(closest - previousKcal)
            ? calories
            : closest,
        undefined,
      );

    form.setValue("kcal", closestCalories?.toString() || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  if (!diet.variants) return null;
  return (
    <div className="space-y-8">
      <FormSection label={t("cart.configuration.variants")}>
        <div className="flex w-full space-x-2">
          <FormButtonSelect
            actionAfterChange={handleChangeMeals}
            fieldName="variant"
            fieldOptions={diet.variants
              .sort((a, b) => a.position - b.position)
              .map((item) => ({
                label: item.name,
                value: item.id,
                description: item.mealTypes
                  .sort((a, b) => a.position - b.position)
                  .map((meal) => meal.name)
                  .join(", "),
              }))}
            form={form}
            layout="grid"
            checkPosition="left"
          />
        </div>
      </FormSection>
      <FormSection
        action={<CaloriesCalculatorTrigger className="hidden md:inline-flex" />}
        contentClassName="pb-2 md:pb-6"
        label={t("cart.configuration.caloricity")}
      >
        <>
          <div className="flex w-full flex-col items-start gap-2 md:flex-row md:items-center">
            <FormButtonSelect
              className="flex-row flex-wrap overflow-x-auto pb-2 md:flex-nowrap md:pb-0 [&>div]:w-auto"
              fieldName="kcal"
              fieldOptions={calorifics
                .sort((a, b) => a.calories - b.calories)
                .map((item) => ({
                  label: (
                    <span className="flex w-full items-center justify-between md:block">
                      <span>
                        {item.calories} {t("common.kcalString")}
                      </span>
                    </span>
                  ),
                  value: item.calories.toString(),
                }))}
              form={form}
            />
          </div>
          {/* Mobile */}
          <CaloriesCalculatorTrigger
            className="my-2 md:hidden"
            variant="button"
          />
        </>
      </FormSection>
    </div>
  );
};
